import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  FileProtectOutlined,
} from '@ant-design/icons';
import { Button } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import type { SortOrder } from 'antd/lib/table/interface';
import dayjs from 'dayjs';
import { filesize } from 'filesize';

import FilterAutoComplete from '@totem/components/common/filterAutoComplete/FilterAutoComplete';
import { getFilterKeys } from '@totem/components/common/filterAutoComplete/FilterAutoCompleteTypes';
import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import { EMPTY_ID } from '@totem/components/common/referencesModal/ReferencesModal';
import DeviceBackupContext from '@totem/components/services/backup/deviceBackups/deviceBackupContext';
import { BackupRecord } from '@totem/components/services/backup/deviceBackups/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import colors from '@totem/styles/colors';
import { getDashboardPath } from '@totem/utilities/authUtilities';
import { isNotNull } from '@totem/utilities/common';
import controlSystemUtilities from '@totem/utilities/controlSystemsUtilities';
import {
  sortBooleanAscending,
  sortNumberAscending,
} from '@totem/utilities/tableUtilities';

const DeviceBackupTable = () => {
  const {
    deviceFilter,
    loading,
    backupData: backupResults,
    onExport,
    isExporting,
  } = useContext(DeviceBackupContext);
  const { input, handleTableChange, getFilterValues, getFilterOptions } =
    useContext(InputContext);
  const { regions, buildings } = useContext(OrganizationContext);
  const { userProfile } = useContext(UserProfileContext);

  const [deviceFilterVisible, setDeviceFilterVisible] =
    useState<boolean>(false);

  const getFileSize = (size: number): string => {
    if (!size) {
      return '-';
    }

    return filesize(size, { standard: 'jedec' });
  };

  const getContractLine = (device: BackupRecord) => {
    if (
      device.contractNumber !== null &&
      device.contractNumber !== '' &&
      device.contractLineNumber !== null
    ) {
      return (
        <span>
          {device.contractNumber}:{device.contractLineNumber}
        </span>
      );
    }
    return <span>-</span>;
  };

  const getBuildingDisplay = (device: BackupRecord) => {
    if (device.buildingId !== null && device.buildingId !== EMPTY_ID) {
      return (
        <Link to={`/dashboard/buildings/${device.buildingId}`}>
          {device.buildingName}
        </Link>
      );
    } else if (device.buildingName !== null) {
      return <span>{device.buildingName}</span>;
    }
    return <span>-</span>;
  };

  const getStatusIcon = (record: BackupRecord) => {
    if (record.contractService === 'Backup Archival') {
      return (
        <FileProtectOutlined
          title="Backup Archived Only"
          style={{
            color: colors.event.criticality.background.ok,
            fontSize: '20px',
          }}
        />
      );
    }

    if (
      typeof record.lastBackedUpAt === 'undefined' ||
      record.lastBackedUpAt === null ||
      dayjs(record.lastBackedUpAt).year() < 2000
    ) {
      return (
        <CloseCircleOutlined
          title="Not Backed Up"
          style={{
            color: colors.event.criticality.background.critical,
            fontSize: '20px',
          }}
        />
      );
    }

    if (record.isBackupStale && record.backupAgeDays >= 3) {
      return (
        <ExclamationCircleOutlined
          title="Backup Aging"
          style={{
            color: colors.event.criticality.background.critical,
            fontSize: '20px',
          }}
        />
      );
    } else if (record.isBackupStale && record.backupAgeDays >= 2) {
      return (
        <ExclamationCircleOutlined
          title="Backup Aging"
          style={{
            color: colors.event.criticality.background.major,
            fontSize: '20px',
          }}
        />
      );
    } else if (record.isBackupStale) {
      return (
        <CheckCircleOutlined
          title="Backup Aging"
          style={{
            color: colors.event.criticality.background.warning,
            fontSize: '20px',
          }}
        />
      );
    }
    return (
      <CheckCircleOutlined
        title="Backup Current"
        style={{
          color: colors.event.criticality.background.ok,
          fontSize: '20px',
        }}
      />
    );
  };

  const columns: ColumnProps<BackupRecord>[] = [
    {
      title: 'Device',
      dataIndex: 'device.displayName',
      key: 'displayName',
      render: (_, record: BackupRecord) => (
        <Link to={`/dashboard/devices/${record.deviceId}`}>
          {record.displayName}
        </Link>
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder:
        input.sort.field === 'displayName'
          ? (input.sort.direction as SortOrder)
          : null,
      sorter: (compA: BackupRecord, compB: BackupRecord) =>
        compA.displayName === compB.displayName
          ? 0
          : compA.displayName > compB.displayName
            ? 1
            : -1,
      filterDropdown: () => (
        <FilterAutoComplete
          label="Device"
          visible={deviceFilterVisible}
          onClose={() => setDeviceFilterVisible(false)}
          {...deviceFilter}
        />
      ),
      filterDropdownProps: {
        open: deviceFilterVisible,
        onOpenChange: setDeviceFilterVisible,
      },
      filteredValue: getFilterKeys(deviceFilter.applied),
    },
    {
      title: 'Control System',
      dataIndex: 'controlSystem.name',
      key: 'controlSystem',
      render: (_, record: BackupRecord) =>
        record.controlSystemId !== EMPTY_ID && (
          <Link
            to={`${getDashboardPath(userProfile)}/controlsystems/${
              record.controlSystemId
            }`}
          >
            {record.controlSystemName}
          </Link>
        ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder:
        input.sort.field === 'controlSystem'
          ? (input.sort.direction as SortOrder)
          : null,
      sorter: (compA: BackupRecord, compB: BackupRecord) =>
        compA.controlSystemName === compB.controlSystemName
          ? 0
          : compA.controlSystemName > compB.controlSystemName
            ? 1
            : -1,
    },
    {
      title: 'Control System Type',
      dataIndex: 'controlSystem.systemType',
      key: 'systemType',
      render: (_, record: BackupRecord) => {
        const option =
          controlSystemUtilities.controlSystemTypeMap[record.controlSystemType];
        return option ? option.text : '';
      },
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder:
        input.sort.field === 'systemType'
          ? (input.sort.direction as SortOrder)
          : null,
      sorter: (compA: BackupRecord, compB: BackupRecord) =>
        compA.controlSystemType === compB.controlSystemType
          ? 0
          : compA.controlSystemType > compB.controlSystemType
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('systemType'),
      filters: getFilterOptions('systemType'),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      render: (_, record: BackupRecord) => {
        return record.lastBackedUpAt !== null &&
          dayjs(record.lastBackedUpAt).year() > 2000
          ? new Date(record.lastBackedUpAt).toLocaleDateString()
          : '-';
      },
    },
    {
      title: 'Time',
      dataIndex: 'time',
      render: (_, record: BackupRecord) => {
        return record.lastBackedUpAt !== null &&
          dayjs(record.lastBackedUpAt).year() > 2000
          ? new Date(record.lastBackedUpAt).toLocaleTimeString()
          : '-';
      },
    },
    {
      title: 'Backup Size',
      dataIndex: 'size',
      render: (_, record: BackupRecord) => {
        return record.lastBackupSize !== null
          ? getFileSize(record.lastBackupSize)
          : '-';
      },
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder:
        input.sort.field === 'size'
          ? (input.sort.direction as SortOrder)
          : null,
      sorter: (compA: BackupRecord, compB: BackupRecord) =>
        sortNumberAscending(compA.lastBackupSize, compB.lastBackupSize),
    },
    {
      title: 'Backup Status',
      dataIndex: 'device.lastBackup.status',
      key: 'isBackupStale',
      render: (_, record: BackupRecord) => {
        return getStatusIcon(record);
      },
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder:
        input.sort.field === 'isBackupStale'
          ? (input.sort.direction as SortOrder)
          : null,
      sorter: (compA: BackupRecord, compB: BackupRecord) =>
        sortBooleanAscending(compA.isBackupStale, compB.isBackupStale),
      filterMultiple: false,
      filteredValue: getFilterValues('isBackupStale'),
      filters: [
        {
          text: (
            <span>
              <CheckCircleOutlined
                title="Current"
                style={{
                  color: colors.event.criticality.background.ok,
                  fontSize: '20px',
                }}
              />
              &nbsp;Current
            </span>
          ),
          value: 'false',
        },
        {
          text: (
            <span>
              <ExclamationCircleOutlined
                title="Stale"
                style={{
                  color: colors.event.criticality.background.critical,
                  fontSize: '20px',
                }}
              />
              &nbsp;Stale
            </span>
          ),
          value: 'true',
        },
      ],
    },
    {
      title: 'Building',
      dataIndex: 'building.name',
      key: 'building',
      render: (_, record: BackupRecord) => getBuildingDisplay(record),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder:
        input.sort.field === 'building'
          ? (input.sort.direction as SortOrder)
          : null,
      sorter: (compA: BackupRecord, compB: BackupRecord) =>
        compA.buildingName === compB.buildingName
          ? 0
          : compA.buildingName > compB.buildingName
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('building'),
      filters:
        typeof buildings !== 'undefined' && buildings !== null
          ? buildings.map((building) => {
              return { text: building.name, value: building.id };
            })
          : [],
    },
    {
      title: 'Region',
      dataIndex: 'region.name',
      key: 'region',
      render: (_, record: BackupRecord) => record.regionName,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder:
        input.sort.field === 'region'
          ? (input.sort.direction as SortOrder)
          : null,
      sorter: (compA: BackupRecord, compB: BackupRecord) =>
        compA.regionName === compB.regionName
          ? 0
          : compA.regionName > compB.regionName
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('region'),
      filters:
        typeof regions !== 'undefined' && regions !== null
          ? regions.map((region) => {
              return { text: region.name, value: region.id };
            })
          : [],
    },
  ];

  const getColumns = () => {
    if (permissions.contractsViewer.hasPermissions(userProfile)) {
      const contractLineCol = {
        title: (
          <IBTooltip content={permissions.contractsViewer.tooltip}>
            Contract Line
          </IBTooltip>
        ),
        dataIndex: 'device.contractLine.orderNumber',
        key: 'backupStatus',
        render: (_, record: BackupRecord) => {
          return getContractLine(record);
        },
      };

      columns.splice(7, 0, contractLineCol);
    }

    return columns;
  };

  const getBackups = () => {
    if (backupResults !== null) {
      if (backupResults.items !== null) {
        return backupResults.items;
      }
    }
    return [];
  };

  return (
    <IBTable
      showSorterTooltip
      columns={getColumns()}
      dataSource={getBackups()}
      loading={loading}
      onChange={handleTableChange}
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        total:
          isNotNull(backupResults) && isNotNull(backupResults.paging)
            ? backupResults.paging.totalRecords
            : 0,
        showSizeChanger: true,
      }}
      rowKey={(record) => record.deviceId}
      title={() => (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            icon={<DownloadOutlined />}
            loading={isExporting}
            onClick={onExport}
          >
            Export CSV
          </Button>
        </div>
      )}
    />
  );
};

export default DeviceBackupTable;
